<script setup lang="ts">
import {ref} from 'vue';
import ResponsiveMenuItem from '@/vue/ResponsiveMenuItem.vue';
import MenuButton from '@/vue/MenuButton.vue';
import {useScrollUpReveal} from "@/vue/composables/useScrollUpReveal.ts";

const { isHidden } = useScrollUpReveal(50);

const openedItemId = ref(null);
const isMenuOpened = ref(false);

const props = defineProps({
  ariaLabel: {type: String, default: null},
  nav: {type: Array, required: true},
});

function toggleItem(itemId, isOpen) {
  openedItemId.value = isOpen ? itemId : null;
}
</script>

<template>
  <nav class="responsive-menu flex flex-col xl:flex-row bg-white shadow-md" :class="{
    'responsive-menu--is-open': isMenuOpened,
    'responsive-menu--is-revealed': !isHidden,
  }">
    <div class="responsive-menu__top-bar flex justify-between xl:self-center items-center h-20 px-5 bg-white">
      <slot name="logo" />
      <menu-button class=" responsive-menu__button" type="collapse" v-model="isMenuOpened" />
    </div>


    <div class="responsive-menu__menu-container xl:ml-4">
      <div class="flex flex-col h-full justify-between xl:items-end mx-2.5 xl:ml-0">
        <slot name="subsidiary-nav" />

        <ol class="responsive-menu__menu">
          <responsive-menu-item v-for="(item, i) in nav"
                                :key="i"
                                :item="item"
                                :is-open="openedItemId === i"
                                class="responsive-menu__item"
                                @toggle="(isOpen) => toggleItem(i, isOpen)"
          />

          <slot name="cta" />
        </ol>
      </div>
    </div>
  </nav>
</template>

<style lang="scss" scoped>
.responsive-menu {
  @apply relative;

  transform: translateY(-100%);
  transition: transform 250ms ease-out;

  &--is-revealed {
    transform: translate(0);
  }

  &__menu-container {
    height: 0;
    overflow: hidden;
    transition: height 200ms ease-out;
  }

  &__menu {
    @apply flex flex-col xl:flex-row xl:items-end w-full h-full uppercase gap-x-4;
    overflow-y: auto;
    overflow-x: visible;
  }

  &__item {
    @apply border-b last:border-0 border-primary xl:border-black xl:font-black;
  }

  &--is-open &__menu-container {
    height: calc(100vh - 80px);
    padding-bottom: 100px;

    @supports (height: 100dvh) {
      height: calc(100dvh - 80px);
      padding-bottom: 0;
    }
  }

  @screen xl {
    &--is-open &__menu-container {
      height: auto;
      padding-bottom: initial;
    }
  }

  @screen xl {
    & {
      height: 130px;
    }

    &__menu-container {
      @apply h-auto overflow-visible;
    }

    &__menu {
      @apply flex items-center overflow-visible;
    }

    &__button {
      @apply hidden;
    }

    &__item {
      @apply border-0;
    }
  }
}

:global(#vue-root) {
  @apply pt-[80px] xl:pt-[130px];
}
</style>
